import { Table, Button, Tag } from "antd";
import { ClockCircleOutlined } from "@ant-design/icons";
import "./OrdersList.scss";
import { useEffect, useState, useCallback, useMemo } from "react";
import { getApiRequest } from "../../../../services/getApiRequest";
import { END_POINTS } from "../../../../Helper/Constant";
import { showToast } from "../../../../shared/sharedComponents/ToasterMessage/ToasterMessage";
import { useTranslation } from "react-i18next";
import i18next from "i18next";
import { useDispatch } from "react-redux";
import {
  hideLoader,
  showLoader,
} from "../../../../features/loader/loaderSlice";
import { useNavigate } from "react-router-dom";
import coin from "../../../../assets/icons/header-coin.svg";

// Expiry Countdown Component
// eslint-disable-next-line react/prop-types
const ExpiryCountdown = ({ expiryDate, t }) => {
  const [timeLeft, setTimeLeft] = useState(null);

  useEffect(() => {
    if (!expiryDate) return;

    const calculateTimeLeft = () => {
      const now = new Date().getTime();
      const expiry = new Date(expiryDate).getTime();
      const difference = expiry - now;

      if (difference <= 0) {
        return {
          expired: true,
          days: 0,
          hours: 0,
          minutes: 0,
          seconds: 0,
        };
      }

      return {
        expired: false,
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor(
          (difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
        ),
        minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((difference % (1000 * 60)) / 1000),
      };
    };

    // Initial calculation
    setTimeLeft(calculateTimeLeft());

    // Update every second
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(timer);
  }, [expiryDate]);

  if (!expiryDate) {
    return <span className="no-expiry">-</span>;
  }

  if (!timeLeft) {
    return <span className="loading-expiry">...</span>;
  }

  if (timeLeft.expired) {
    return (
      <Tag color="red" className="expired-tag">
        {t("orders.expired") || "EXPIRED"}
      </Tag>
    );
  }

  // Color coding based on days left
  const getColorClass = () => {
    if (timeLeft.days <= 7) return "expiry-critical"; // Red
    if (timeLeft.days <= 30) return "expiry-warning"; // Orange
    return "expiry-safe"; // Green
  };

  return (
    <div className={`expiry-countdown ${getColorClass()}`}>
      <ClockCircleOutlined style={{ marginRight: 5 }} />
      <div className="countdown-values">
        {timeLeft.days > 0 && (
          <span className="countdown-item">
            <strong>{timeLeft.days}</strong>
            {t("orders.days_short") || "d"}
          </span>
        )}
        <span className="countdown-item">
          <strong>{String(timeLeft.hours).padStart(2, "0")}</strong>
          {t("orders.hours_short") || "h"}
        </span>
        <span className="countdown-item">
          <strong>{String(timeLeft.minutes).padStart(2, "0")}</strong>
          {t("orders.minutes_short") || "m"}
        </span>
        <span className="countdown-item">
          <strong>{String(timeLeft.seconds).padStart(2, "0")}</strong>
          {t("orders.seconds_short") || "s"}
        </span>
      </div>
    </div>
  );
};

const OrdersList = () => {
  const { t } = useTranslation("common");
  const [orders, setOrders] = useState([]);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
    getAllOrders();
  }, []);

  // Memoized API call function
  const getAllOrders = useCallback(async () => {
    dispatch(showLoader());
    try {
      const response = await getApiRequest(END_POINTS.FETCH_ALL_ORDERS);
      console.log("response: ", response);
      if (response?.success) {
        const formattedData =
          response?.data?.map((order, index) => ({
            key: order._id || index,
            order: `#${order.reference_id || "N/A"}`,
            date: new Date(order.createdAt).toLocaleDateString(
              i18next.language === "ar" ? "ar-SA" : "en-US",
              { year: "numeric", month: "long", day: "2-digit" }
            ),
            status: order.status?.toUpperCase() || "PENDING",
            total: order.amountInCoins || 0,
            items: 1,
            product: order.product || {},
            gift_voucher: order.gift_voucher || [],
            expiry_date: order.expiry_date || null,
          })) || [];

        setOrders(formattedData);
      }
    } catch (error) {
      console.log("error: ", error);
      showToast(
        error?.response?.data?.message ||
          error?.error?.message ||
          "Failed to fetch orders",
        "error"
      );
    } finally {
      dispatch(hideLoader());
    }
  }, [dispatch]);

  // Memoized navigation handler
  const handleViewOrder = useCallback(
    (record) => {
      navigate("/order-details", { state: { order: record } });
    },
    [navigate]
  );

  // Memoized text alignment
  const textAlignment = useMemo(
    () => ({
      textAlign: i18next.language === "ar" ? "right" : "left",
    }),
    []
  );

  // Memoized table columns
  const columns = useMemo(
    () => [
      {
        title: t("orders.columns.order"),
        dataIndex: "order",
        key: "order",
        width: 200,
        render: (text) => (
          <span
            className="order-id"
            style={{ color: "#00ff90", fontWeight: 600 }}
          >
            {text}
          </span>
        ),
      },
      {
        title: t("orders.columns.product_name"),
        dataIndex: "product",
        key: "product",
        width: 200,
        render: (product) => (
          <span className="product-name" style={{ fontWeight: 500 }}>
            {product?.name}
          </span>
        ),
      },
      {
        title: t("orders.columns.date"),
        dataIndex: "date",
        key: "date",
        width: 180,
        render: (date) => (
          <span className="order-date" style={{ color: "#bfbfbf" }}>
            {date}
          </span>
        ),
      },
      {
        title: t("orders.columns.status"),
        dataIndex: "status",
        key: "status",
        width: 150,
        render: (status) => (
          <span
            className={`status ${status.toLowerCase()}`}
            style={{ color: "#bfbfbf", fontWeight: 600 }}
          >
            {status}
          </span>
        ),
      },
      {
        title: t("orders.columns.expiry"),
        dataIndex: "expiry_date",
        key: "expiry_date",
        width: 220,
        render: (expiry_date) => (
          <div className="timer-box">
            <ExpiryCountdown expiryDate={expiry_date} t={t} />
          </div>
        ),
      },
      {
        title: t("orders.columns.total"),
        dataIndex: "total",
        key: "total",
        width: 140,
        render: (total) => (
          <span className="total" style={{ fontWeight: 600 }}>
            <img src={coin} alt="coin" className="market-place-coin" />{" "}
            {total.toLocaleString()}
          </span>
        ),
      },
      {
        title: t("orders.columns.action"),
        key: "action",
        width: 140,
        render: (record) => (
          <Button
            type="primary"
            className="view-btn"
            onClick={() => handleViewOrder(record)}
            style={{
              background: "#00ffa7",
              borderColor: "#00ffa7",
              color: "#000",
              fontWeight: 600,
              borderRadius: "6px",
              padding: "4px 18px",
            }}
          >
            {t("orders.view_button")}
          </Button>
        ),
      },
    ],
    [t, handleViewOrder]
  );

  // Memoized header component
  const headerComponent = useMemo(
    () => (
      <div className="orders-header" style={textAlignment}>
        <h3>{t("orders.title")}</h3>
        {/* <p>{t("orders.subtitle")}</p> */}
      </div>
    ),
    [t, textAlignment]
  );

  return (
    <section className="order-section">
      <div className="container">
        <div className="order-table">
          {headerComponent}
          <Table
            columns={columns}
            dataSource={orders}
            pagination={true}
            className="orders-table"
            scroll={{ x: "1200px" }}
          />
        </div>
      </div>
    </section>
  );
};

export default OrdersList;
